/**
 * TaskStatusBadge Component
 * Displays task status with color coding and icon
 * Fetches name/color from API cache when given only an ID
 */

import { Badge, BadgeProps } from '@mantine/core';
import {
  IconRefresh,
  IconPlayerPause,
  IconEye,
  IconEyeCheck,
  IconPlayerPlay,
  IconCheck,
  IconLock,
} from '@tabler/icons-react';
import { useTaskLookups } from '@/hooks/tasks/useTaskLookups';

interface TaskStatusObj {
  statusID: number;
  name: string;
  color?: string;
}

interface TaskStatusBadgeProps extends Omit<BadgeProps, 'children'> {
  status?: TaskStatusObj | number;
  statusId?: number;
  showIcon?: boolean;
}

// Icon mapping by status ID
const STATUS_ICONS: Record<number, React.ReactNode> = {
  [-2]: <IconRefresh size={12} />,
  [-1]: <IconPlayerPause size={12} />,
  [0]: <IconEye size={12} />,
  [1]: <IconEyeCheck size={12} />,
  [2]: <IconPlayerPlay size={12} />,
  [3]: <IconCheck size={12} />,
  [4]: <IconLock size={12} />,
};

// Mantine color mapping from hex colors
const HEX_TO_MANTINE: Record<string, string> = {
  '#FF0000': 'red',
  '#FFA500': 'orange',
  '#0000FF': 'blue',
  '#26bad9': 'cyan',
  '#FFFF00': 'yellow',
  '#00FF00': 'green',
  '#808080': 'gray',
};

function hexToMantineColor(hex?: string | null): string {
  if (!hex) return 'gray';
  return HEX_TO_MANTINE[hex.toUpperCase()] || 'gray';
}

export function TaskStatusBadge({
  status,
  statusId,
  showIcon = true,
  variant = 'light',
  size = 'sm',
  ...props
}: TaskStatusBadgeProps) {
  const { getStatusById } = useTaskLookups();

  // Resolve the status ID
  const resolvedId = statusId ?? (typeof status === 'number' ? status : status?.statusID);

  if (resolvedId === undefined || resolvedId === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  // Get name/color from the passed object or from API cache
  let label: string;
  let color: string;

  if (typeof status === 'object' && status?.name) {
    label = status.name;
    color = hexToMantineColor(status.color);
  } else {
    const apiStatus = getStatusById(resolvedId);
    label = apiStatus?.name ?? `Status ${resolvedId}`;
    color = hexToMantineColor(apiStatus?.color);
  }

  const icon = STATUS_ICONS[resolvedId];

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}
